import { CheckCheck, Clock, FileInput, Undo2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import {
  DESTINATION,
  DESTINATION_LABEL,
  REQUEST_KIND,
  REQUEST_STATUS,
  type EvidenceRequestDetail,
  type EvidenceRequestStatus,
} from '../../../evidence-management/evidence/evidenceTypes';
import { usePersonName } from '../../../human-resources/personnel/useStaff';
import { EvidenceRequestItemsPanel } from '../components/EvidenceRequestItemsPanel';
import { evidenceRequestsApi } from '../evidenceRequestsApi';

type TabKey = 'info' | 'items';

/** A profile may wear `pending`; the index never does. */
const STATUS_TONE: Record<EvidenceRequestStatus, StatusTone> = {
  [REQUEST_STATUS.pending]: 'pending',
  [REQUEST_STATUS.fulfilled]: 'active',
  [REQUEST_STATUS.declined]: 'inactive',
  [REQUEST_STATUS.cancelled]: 'inactive',
};

/**
 * One evidence request, as its REQUESTER follows it.
 *
 * **IT LANDS ON ITEMS** — a request is opened to see WHAT WAS ASKED FOR, the requisition slip's call
 * one module along. `Info` is still the first tab DECLARED.
 *
 * **No card carries an Edit, and that is the design, not a gap**: a request is raised whole and then
 * DECIDED — fulfilled or declined by the case's team, or cancelled here — so its fields are never
 * corrected after they are handed over, and the module has no update key that could.
 */
export function EvidenceRequestProfilePage() {
  const { id = '' } = useParams();
  const toast = useToast();

  const [request, setRequest] = useState<EvidenceRequestDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('items');
  const [cancelling, setCancelling] = useState(false);
  const [acting, setActing] = useState(false);

  const judge = usePersonName(request?.judgePersonId);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setRequest(await evidenceRequestsApi.get(id, signal));
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load this request.', 0));
      }
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function cancel() {
    setActing(true);
    setError(null);

    try {
      const next = await evidenceRequestsApi.cancel(id);
      setRequest(next);
      setCancelling(false);
      toast.success(`${next.requestNumber} was cancelled.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not cancel this request.', 0));
    } finally {
      setActing(false);
    }
  }

  // A failed REFRESH must not replace the page — only a first load that failed does.
  if (error !== null && request === null) {
    return (
      <div role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-3">
        <p className="text-xs text-red-700">{error.message}</p>
      </div>
    );
  }

  if (request === null) {
    return null;
  }

  const banner = bannerFor(request);
  const BannerIcon = banner.icon;
  const isTransfer = request.kind === REQUEST_KIND.transfer;
  const toCourt = request.destination === DESTINATION.court;
  const caseLabel = request.isRestricted ? request.caseNumber : `${request.caseNumber} — ${request.caseTitle}`;

  const breadcrumb: BreadcrumbSegment[] = [
    'Self Service',
    'Evidence',
    { label: 'Evidence Request', to: '/self-service/evidence-requests' },
    request.requestNumber,
  ];

  return (
    <>
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="evidence-request-profile"
        // Info is a stack of cards; Items is a table owning its own chrome and scroll.
        panel={tab === 'items' ? 'bleed' : 'cards'}
        header={
          <ProfileHeader
            id="evidence-request-profile-header"
            // The sidebar entry's glyph, so the header and the nav row carry one symbol.
            icon={<FileInput className="h-6 w-6" aria-hidden="true" />}
            title={request.requestNumber}
            meta={[`${request.kindLabel} request`, caseLabel]}
            secondary={[
              `${request.lines.length} ${request.lines.length === 1 ? 'item' : 'items'}`,
              request.destination != null ? `To ${DESTINATION_LABEL[request.destination].toLowerCase()}` : null,
            ].filter(Boolean) as string[]}
            status={<StatusText tone={STATUS_TONE[request.status]}>{request.statusLabel}</StatusText>}
            createdAt={request.createdAt}
            createdBy={request.createdByName}
          />
        }
        banner={
          // EVERY state has a sentence; Cancel — the record-level lifecycle action — lives here.
          <NextActionAlert
            icon={<BannerIcon className="h-4 w-4" aria-hidden="true" />}
            title={banner.title}
            description={banner.description}
            action={
              request.status === REQUEST_STATUS.pending && request.isActive ? (
                <PermissionGate permission="evidence-requests.cancel">
                  <Button
                    id="evidence-request-cancel"
                    variant="secondary"
                    leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setCancelling(true)}
                    disabled={acting}
                  >
                    Cancel request
                  </Button>
                </PermissionGate>
              ) : null
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'items',
            label: (
              <>
                Items <CountBadge count={request.lines.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {error !== null ? (
          <div role="alert" className="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {tab === 'info' ? (
          <>
            {/* NO Edit — a request is never corrected after it is handed over (see the page's own note). */}
            <ProfileCard id="evidence-request-profile-request" title="Request">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Request No.', value: request.requestNumber, mono: true },
                  {
                    label: 'Case',
                    value: request.isRestricted ? (
                      <>
                        <span className="font-mono">{request.caseNumber}</span>
                        <span className="sr-only"> — title withheld, you can no longer open this case</span>
                      </>
                    ) : (
                      caseLabel
                    ),
                  },
                  { label: 'Type', value: request.kindLabel },
                  ...(isTransfer
                    ? [{ label: 'Transfer to', value: request.destination != null ? DESTINATION_LABEL[request.destination] : null }]
                    : []),
                  ...(isTransfer && toCourt
                    ? [
                        { label: 'Court Branch', value: request.courtBranch },
                        { label: 'Court Order No.', value: request.courtOrderNumber, mono: true },
                        { label: 'Court order file', value: request.courtOrderFile },
                        { label: 'Judge', value: judge },
                      ]
                    : []),
                  ...(isTransfer && !toCourt
                    ? [
                        { label: 'Disposal Type', value: request.disposalTypeName },
                        { label: 'Reason', value: request.reason },
                      ]
                    : []),
                ]}
              />
            </ProfileCard>

            {/* Moved by an ACTION — the team's, or this page's Cancel — never by typing; so no Edit. */}
            <ProfileCard id="evidence-request-profile-outcome" title="Outcome">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Stage', value: request.statusLabel },
                  {
                    label: 'Decided',
                    value: request.decidedAt == null ? null : formatDateTime(request.decidedAt),
                    mono: true,
                  },
                  { label: 'Decided by', value: request.decidedByName },
                  { label: 'Declined because', value: request.declineReason },
                  ...(isTransfer ? [] : [{ label: 'Receiving No.', value: request.receivingNumber, mono: true }]),
                ]}
              />
            </ProfileCard>

            <ProfileCard id="evidence-request-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created', value: formatDateTime(request.createdAt), mono: true },
                  { label: 'Created by', value: request.createdByName },
                  {
                    label: 'Latest update',
                    value: request.updatedAt == null ? null : formatDateTime(request.updatedAt),
                    mono: true,
                  },
                  { label: 'Updated by', value: request.updatedByName },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          // A `bleed` panel gives its child no gap — the inset belongs to this wrapper; `h-full` feeds `fill`.
          <div className="flex h-full min-w-0 flex-col p-4">
            <EvidenceRequestItemsPanel request={request} />
          </div>
        )}
      </ProfileHeaderShell>

      {cancelling ? (
        <ConfirmDialog
          open
          id="evidence-request-profile-cancel-dialog"
          title={`Cancel ${request.requestNumber}`}
          // A banner action that WRITES confirms with the tick. No password — an ordinary act of the job.
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-xs text-slate-600">
              <p>
                The request leaves the case&apos;s Requests and stays here as Cancelled. Nothing was received or
                transferred, and a cancelled request cannot be reopened — raise a new one if it is still needed.
              </p>
              {error ? <p className="text-red-700">{error.message}</p> : null}
            </div>
          }
          loading={acting}
          onConfirm={() => void cancel()}
          onClose={() => {
            setCancelling(false);
            setError(null);
          }}
        />
      ) : null}
    </>
  );
}

/** What the banner says for EVERY stage — there is no null case. */
function bannerFor(request: EvidenceRequestDetail): {
  tone: 'blue' | 'emerald' | 'amber' | 'slate' | 'red';
  icon: typeof Clock;
  title: string;
  description: string;
} {
  const isTransfer = request.kind === REQUEST_KIND.transfer;

  switch (request.status) {
    case REQUEST_STATUS.pending:
      return {
        tone: 'blue',
        icon: Clock,
        title: "Waiting for the case's team",
        description: isTransfer
          ? "It is listed under the case's Requests. A member raises the transfer — with their own password, and through approval — or declines it. Nothing has moved."
          : "It is listed under the case's Requests. A member receives the items — your documents ride along — or declines it. Nothing has been received.",
      };

    case REQUEST_STATUS.fulfilled:
      return isTransfer
        ? {
            tone: 'emerald',
            icon: CheckCheck,
            title: 'Transfer raised',
            description:
              "The case's team raised the transfer you asked for. It now waits for approval; the items move only once it is approved.",
          }
        : {
            tone: 'emerald',
            icon: CheckCheck,
            title: request.receivingNumber ? `Received as ${request.receivingNumber}` : 'Received',
            description: "The case's team received the items. Each now has its own inventory number and waits for evaluation.",
          };

    case REQUEST_STATUS.declined:
      return {
        tone: 'red',
        icon: XCircle,
        title: 'Declined',
        description: request.declineReason
          ? `The case's team declined it: ${request.declineReason}`
          : "The case's team declined it.",
      };

    default:
      return {
        tone: 'slate',
        icon: Undo2,
        title: 'Cancelled',
        description: 'You cancelled this request before the case’s team acted on it. Nothing was received or transferred.',
      };
  }
}
